import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssetAgeHistogram = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showDepreciation, setShowDepreciation] = useState(false);

  const ageData = [
    { 
      ageRange: '0-1 Years', 
      count: 89, 
      value: 445000, 
      depreciation: 5,
      priority: 'low',
      color: '#059669',
      laptops: 45, desktops: 25, servers: 12, printers: 4, monitors: 3
    },
    { 
      ageRange: '1-2 Years', 
      count: 124, 
      value: 558000, 
      depreciation: 15,
      priority: 'low',
      color: '#0EA5E9',
      laptops: 62, desktops: 35, servers: 15, printers: 8, monitors: 4
    },
    { 
      ageRange: '2-3 Years', 
      count: 98, 
      value: 392000, 
      depreciation: 35,
      priority: 'medium',
      color: '#D97706',
      laptops: 48, desktops: 28, servers: 12, printers: 6, monitors: 4
    },
    { 
      ageRange: '3-4 Years', 
      count: 67, 
      value: 201000, 
      depreciation: 55,
      priority: 'high',
      color: '#DC2626',
      laptops: 32, desktops: 18, servers: 8, printers: 5, monitors: 4
    },
    { 
      ageRange: '4-5 Years', 
      count: 34, 
      value: 85000, 
      depreciation: 75,
      priority: 'critical',
      color: '#7C3AED',
      laptops: 15, desktops: 10, servers: 4, printers: 3, monitors: 2
    },
    { 
      ageRange: '5+ Years', 
      count: 18, 
      value: 27000, 
      depreciation: 90,
      priority: 'critical',
      color: '#EF4444',
      laptops: 8, desktops: 6, servers: 2, printers: 1, monitors: 1
    }
  ];

  const replacementRecommendations = [
    {
      id: 'RR-001',
      category: 'Laptops',
      ageRange: '4-5 Years',
      count: 15,
      estimatedCost: 18750,
      urgency: 'high',
      reason: 'Performance degradation and increased maintenance costs'
    },
    {
      id: 'RR-002',
      category: 'Servers',
      ageRange: '5+ Years',
      count: 2,
      estimatedCost: 25000,
      urgency: 'critical',
      reason: 'End of vendor support and security vulnerabilities'
    },
    {
      id: 'RR-003',
      category: 'Desktops',
      ageRange: '4-5 Years',
      count: 10,
      estimatedCost: 8000,
      urgency: 'medium',
      reason: 'Hardware compatibility issues with new software'
    },
    {
      id: 'RR-004',
      category: 'Printers',
      ageRange: '3-4 Years',
      count: 5,
      estimatedCost: 2500,
      urgency: 'low',
      reason: 'Increased toner costs and maintenance frequency'
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevated">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Assets:</span>
              <span className="text-sm font-medium text-popover-foreground">{data?.count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Total Value:</span>
              <span className="text-sm font-medium text-popover-foreground">₹{data?.value?.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Depreciation:</span>
              <span className="text-sm font-medium text-popover-foreground">{data?.depreciation}%</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <p className="text-xs text-muted-foreground mb-1">Breakdown:</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div>Laptops: {data?.laptops}</div>
                <div>Desktops: {data?.desktops}</div>
                <div>Servers: {data?.servers}</div>
                <div>Printers: {data?.printers}</div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'high':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'medium':
        return 'text-accent bg-accent/10 border-accent/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical':
        return 'AlertTriangle';
      case 'high':
        return 'Clock';
      case 'medium':
        return 'Info';
      case 'low':
        return 'CheckCircle';
      default:
        return 'Minus';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Asset Age Analysis</h3>
          <p className="text-sm text-muted-foreground">Equipment lifecycle and replacement planning</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={showDepreciation ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowDepreciation(!showDepreciation)}
            iconName="TrendingDown"
            iconPosition="left"
          >
            Depreciation
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            iconPosition="left"
          >
            Report
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="xl:col-span-2">
          <div className="h-80 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="ageRange" 
                  tick={{ fontSize: 12 }}
                  stroke="#64748B"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#64748B"
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey={showDepreciation ? "depreciation" : "count"} 
                  radius={[4, 4, 0, 0]}
                >
                  {ageData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Age Distribution Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-success/5 rounded-lg border border-success/20">
              <div className="text-xl font-bold text-success">213</div>
              <div className="text-xs text-muted-foreground">0-2 Years</div>
              <div className="text-xs text-success">Excellent</div>
            </div>
            <div className="text-center p-3 bg-accent/5 rounded-lg border border-accent/20">
              <div className="text-xl font-bold text-accent">98</div>
              <div className="text-xs text-muted-foreground">2-3 Years</div>
              <div className="text-xs text-accent">Good</div>
            </div>
            <div className="text-center p-3 bg-warning/5 rounded-lg border border-warning/20">
              <div className="text-xl font-bold text-warning">67</div>
              <div className="text-xs text-muted-foreground">3-4 Years</div>
              <div className="text-xs text-warning">Monitor</div>
            </div>
            <div className="text-center p-3 bg-error/5 rounded-lg border border-error/20">
              <div className="text-xl font-bold text-error">52</div>
              <div className="text-xs text-muted-foreground">4+ Years</div>
              <div className="text-xs text-error">Replace</div>
            </div>
          </div>
        </div>

        {/* Replacement Recommendations */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-card-foreground">Replacement Priority</h4>
            <Button variant="ghost" size="sm" iconName="Settings">
              Configure
            </Button>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {replacementRecommendations?.map((item) => (
              <div
                key={item?.id}
                className="p-3 border border-border rounded-lg hover:border-primary/50 transition-smooth"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-card-foreground">
                        {item?.category}
                      </span>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item?.urgency)}`}>
                        <Icon name={getUrgencyIcon(item?.urgency)} size={12} className="inline mr-1" />
                        {item?.urgency?.toUpperCase()}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {item?.ageRange} • {item?.count} assets
                    </p>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                  {item?.reason}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    Est. Cost: <span className="font-medium text-card-foreground">
                      ₹{item?.estimatedCost?.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                    Plan
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Total Replacement Cost */}
          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-card-foreground">
                Total Replacement Budget
              </span>
              <Icon name="Calculator" size={16} className="text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-card-foreground">
              ₹54,250
            </div>
            <p className="text-xs text-muted-foreground">
              For 32 priority assets over next 12 months
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetAgeHistogram;